import { Box, Button, Divider, MenuItem, Stack, StackProps, TextField, Typography } from '@mui/material'
import { useAtom } from 'jotai'
import { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, FieldErrors, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import {
  AddressValidationCorrection,
  AddressValidationIssue,
  AddressValidationResult,
  GBAddressCountry,
  MonitoringLocationChangeAddress
} from '../../../api/models'
import { useValidateAddressFn } from '../../../api/apiQueries'
import {
  addressValidationResultAtom,
  currentLocationChangeAtom,
  IncompleteLocationChange,
  selectedAddressValidationResultIndexAtom
} from '../../../atoms'
import { RESIDENCE_TYPE_ARRAY, STATE_ABBREVIATION_MAP, VALID_TIME_ZONES } from '../../../constants'
import {
  getUserUserFriendlyMessage,
  IssueMessageForField,
  toAddressCandidateTimeZone,
  toAddressValidationRequest,
  toMonitoringLocationChangeAddressFromCandidate,
  updateCurrentLocationChangeWithUserInput
} from './addressScheduleFormUtils'
import { useAuth0 } from '@auth0/auth0-react'
import { sendLocationChangeValidationErrorMetric } from '../DatePickerPage/metricsUtils'
import { addressValidationPaths } from '../addressValidationPaths'
import { MonitoringLocationChangeAddressCountry } from '../../../api/generated/museum-v1-types'
import { useSnackbar } from '../../../hooks/useSnackbar'
import { EnqueueSnackbar } from 'notistack'

interface AddressSchedulerFormProps extends StackProps {
  sid: string
  title: string
  instructions?: string
  showAdditionalLocationFields?: boolean
  statusChip?: ReactNode
  country?: string
}

/**
 * looks for a candidate with no issues or corrections, or a candidate with only trivial issues and corrections. Candidate must not have a time zone correction.
 * @param addressValidationResult
 * @param updatedLocationChange
 * @param toast
 */
const findCandidateSuitableForAutoSelect = (
  addressValidationResult: AddressValidationResult,
  updatedLocationChange: IncompleteLocationChange,
  toast: EnqueueSnackbar
): number | undefined => {
  // Find a candidate with no issues and no non-trivial corrections
  for (let i = 0; i < addressValidationResult.candidates.length; i++) {
    const candidate = addressValidationResult.candidates[i]
    if (candidate.issues.length === 0 && candidate.corrections.every(correction => correction.severity === 'Trivial')) {
      if (candidate.corrections.length > 0 || toAddressCandidateTimeZone(updatedLocationChange.address?.timeZone) !== candidate.timeZone) {
        toast('Some corrections were automatically applied to address.', { variant: 'info' })
      }
      return i
    }
  }
}

/**
 * When there is only 1 validation candidate AND there is an Issue AND no non-trivial corrections, we need to send use to the confirmation page
 * @param addressValidationResult
 * return the index of the candidate that requires confirmation, or undefined if no candidates require confirmation
 */
const checkIfCandidateRequiresConfirmationPage = (addressValidationResult: AddressValidationResult): number | undefined => {
  if (addressValidationResult.candidates.length === 1) {
    const candidate = addressValidationResult.candidates[0]
    if (
      (candidate.issues.length > 0 && candidate.corrections.every(correction => correction.severity === 'Trivial')) ||
      candidate.issues.some(issue => issue.severity === 'Critical')
    ) {
      return 0
    }
  }
}

export const AddressSchedulerForm: FC<AddressSchedulerFormProps> = ({
  sid,
  title,
  instructions,
  showAdditionalLocationFields,
  statusChip,
  country
}) => {
  const { user } = useAuth0()
  const navigate = useNavigate()
  const { sid: locationId } = useParams()
  const { toast } = useSnackbar()

  const [currentLocationChange, setCurrentLocationChange] = useAtom(currentLocationChangeAtom)
  const [addressValidationResult, setAddressValidationResult] = useAtom(addressValidationResultAtom)
  const [selectedAddressValidationResultIndex, setSelectedAddressValidationResultIndex] = useAtom(selectedAddressValidationResultIndexAtom)
  const [focusedFieldNames, setFocusedFieldNames] = useState<Set<string>>(new Set())
  const [isLoading, setIsLoading] = useState(false)

  const isGBLocation = country === GBAddressCountry.GB

  const validateAddress = useValidateAddressFn()

  const onSubmit = async (formData: MonitoringLocationChangeAddress) => {
    setIsLoading(true)

    try {
      const emptyLocationChange: IncompleteLocationChange = { locationId: locationId! }
      const updatedLocationChange = updateCurrentLocationChangeWithUserInput(currentLocationChange ?? emptyLocationChange, formData)

      // For GB locations, we skip validation and go straight to timezone selection
      if (isGBLocation) {
        if (updatedLocationChange.address) {
          updatedLocationChange.address.state = 'LDN' // GB locations must have a state value of LDN for the BE
          updatedLocationChange.address.country = MonitoringLocationChangeAddressCountry.GB
        }

        setSelectedAddressValidationResultIndex(undefined)
        setCurrentLocationChange(updatedLocationChange)
        navigate(`../${addressValidationPaths.timeZone}`)
        return
      }

      const addressValidationResult = await validateAddress(sid, toAddressValidationRequest(updatedLocationChange.address!))

      setSelectedAddressValidationResultIndex(undefined)
      setAddressValidationResult(addressValidationResult)

      const candidateSuitableForAutoSelect = findCandidateSuitableForAutoSelect(addressValidationResult, updatedLocationChange, toast)
      const candidateRequiresConfirmationPage = checkIfCandidateRequiresConfirmationPage(addressValidationResult)
      const autoSelectedCandidate = candidateSuitableForAutoSelect !== undefined ? candidateSuitableForAutoSelect : candidateRequiresConfirmationPage

      if (autoSelectedCandidate !== undefined) {
        setSelectedAddressValidationResultIndex(autoSelectedCandidate)
        updatedLocationChange.address = toMonitoringLocationChangeAddressFromCandidate(
          addressValidationResult.candidates[autoSelectedCandidate],
          formData
        )
      }
      setCurrentLocationChange(updatedLocationChange)

      // Navigate to next page based on validation results.
      if (candidateSuitableForAutoSelect !== undefined) {
        navigate(`../${addressValidationPaths.datePicker}`)
      } else if (candidateRequiresConfirmationPage !== undefined) {
        navigate(`../${addressValidationPaths.confirmation}`)
      } else {
        navigate(`../${addressValidationPaths.validationResults}`)
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancel = () => {
    // Navigate to previous page
    navigate(-1)
  }

  const selectedValidationResult = useMemo(() => {
    // If returning from validation-results, we need to check for a selectedAddressValidationResult
    if (typeof selectedAddressValidationResultIndex === 'number' && addressValidationResult) {
      return addressValidationResult.candidates[selectedAddressValidationResultIndex]
    }
    return undefined
  }, [addressValidationResult, selectedAddressValidationResultIndex])

  const initialFormValues: MonitoringLocationChangeAddress = useMemo(() => {
    if (selectedValidationResult) {
      return toMonitoringLocationChangeAddressFromCandidate(selectedValidationResult, currentLocationChange?.address)
    } else if (currentLocationChange) {
      return currentLocationChange.address || ({} as MonitoringLocationChangeAddress)
    } else {
      return {} as MonitoringLocationChangeAddress
    }
  }, [currentLocationChange, selectedValidationResult])

  const {
    handleSubmit,
    register,
    formState: { errors },
    control
  } = useForm<MonitoringLocationChangeAddress>({ values: initialFormValues })

  useEffect(() => {
    // reset the focused fields
    setFocusedFieldNames(new Set())

    if (selectedValidationResult) {
      const validationIssues: (AddressValidationIssue | AddressValidationCorrection)[] = [
        ...selectedValidationResult.issues,
        ...selectedValidationResult.corrections
      ]

      if (validationIssues.length > 0) {
        sendLocationChangeValidationErrorMetric(
          user,
          sid,
          validationIssues.map(r => JSON.stringify(r))
        )
      }
    }
  }, [selectedValidationResult, sid, user])

  const nonCriticalIssues = useMemo(() => {
    return selectedValidationResult?.issues.filter(issue => issue.severity !== 'Critical') || []
  }, [selectedValidationResult?.issues])

  const trackFocusedFields = useCallback(
    (fieldName: string) => {
      if (!focusedFieldNames.has(fieldName)) {
        setFocusedFieldNames(new Set([...focusedFieldNames, fieldName]))
      }
    },
    [focusedFieldNames]
  )

  const addressIssuesByField = useMemo(() => {
    const issuesByField: Map<string, IssueMessageForField> = new Map()
    nonCriticalIssues.forEach(issue => {
      const issueMessageForField = getUserUserFriendlyMessage(issue)
      if (issueMessageForField && !focusedFieldNames.has(issueMessageForField.fieldName)) {
        issuesByField.set(issueMessageForField.fieldName, issueMessageForField)
      }
    })
    return issuesByField
  }, [nonCriticalIssues, focusedFieldNames])

  const addValidationBehaviors = useCallback(
    (fieldName: keyof FieldErrors<Partial<MonitoringLocationChangeAddress>>) => {
      return {
        error: !!errors[fieldName] || !!addressIssuesByField.get(fieldName),
        helperText: errors[fieldName]?.message || addressIssuesByField.get(fieldName)?.helperText,
        onFocus: () => trackFocusedFields(fieldName)
      }
    },
    [addressIssuesByField, errors, trackFocusedFields]
  )

  return (
    <form onSubmit={handleSubmit(onSubmit)} data-testid={'address-change-form'}>
      <Stack spacing={0} sx={{ width: '100%', bgcolor: 'var(--bg-surface)', borderRadius: 'var(--border-radius-l)' }}>
        <Stack spacing={'var(--spacing-xl)'} sx={{ p: 'var(--spacing-m)' }}>
          <Stack spacing={'var(--spacing-xs)'}>
            <Stack direction={'row'}>
              <Typography variant="h2" fontSize={'var(--size-xl)'} fontWeight="700" color="text.primary">
                {title}
              </Typography>
              {statusChip && <Box marginInlineStart={'var(--spacing-2xs)'}>{statusChip}</Box>}
            </Stack>
            {instructions && <Typography>{instructions}</Typography>}
          </Stack>
          <Stack spacing={'var(--spacing-m)'}>
            {nonCriticalIssues.map((issue, i) => {
              return (
                <Typography key={i} variant="body1" fontWeight="300" color="text.primary" data-testid={issue.type}>
                  {issue.description.external}
                </Typography>
              )
            })}
            <Stack spacing={'var(--spacing-l)'}>
              <Controller
                name="propertyType"
                control={control}
                rules={{ required: 'Property Type is required' }}
                render={({ field }) => (
                  <TextField
                    value={field.value}
                    onChange={e => {
                      field.onChange(e.target.value)
                    }}
                    {...addValidationBehaviors('propertyType')}
                    slotProps={{
                      // @ts-expect-error - select doesn't expose the data attributes but this is valid
                      select: { ['data-testid']: 'propertyType' }
                    }}
                    select
                    label="Property Type"
                    fullWidth
                  >
                    {RESIDENCE_TYPE_ARRAY.map(type => {
                      return (
                        <MenuItem key={type} data-testid="propertyType_value" value={type}>
                          {type}
                        </MenuItem>
                      )
                    })}
                  </TextField>
                )}
              />
              <Stack direction="row" spacing={'var(--spacing-3xl)'}>
                <Stack spacing={'var(--spacing-l)'} flex={1}>
                  <TextField
                    {...register('street1', {
                      required: 'Address is required'
                    })}
                    {...addValidationBehaviors('street1')}
                    slotProps={{
                      htmlInput: { ['data-testid']: 'street1' }
                    }}
                    label="Address"
                    fullWidth
                  />
                  <TextField
                    {...register('street2', {})}
                    {...addValidationBehaviors('street2')}
                    slotProps={{
                      htmlInput: { ['data-testid']: 'street2' }
                    }}
                    label="Apartment/Suite/Floor"
                    fullWidth
                  />
                  <TextField
                    {...register('city', {
                      required: 'City is required'
                    })}
                    {...addValidationBehaviors('city')}
                    slotProps={{
                      htmlInput: { ['data-testid']: 'city' }
                    }}
                    label="City"
                    fullWidth
                  />
                  {!isGBLocation && (
                    <Controller
                      name="state"
                      control={control}
                      rules={{ required: 'State is required' }}
                      render={({ field }) => (
                        <TextField
                          value={field.value}
                          onChange={e => {
                            field.onChange(e.target.value)
                          }}
                          {...addValidationBehaviors('state')}
                          slotProps={{
                            // @ts-expect-error - select doesn't expose the data attributes but this is valid
                            select: { ['data-testid']: 'state' }
                          }}
                          select
                          label="State"
                          fullWidth
                        >
                          {Object.keys(STATE_ABBREVIATION_MAP).map(type => {
                            return (
                              <MenuItem key={type} data-testid="state_value" value={type}>
                                {STATE_ABBREVIATION_MAP[type]}
                              </MenuItem>
                            )
                          })}
                        </TextField>
                      )}
                    />
                  )}
                  {isGBLocation ? (
                    <TextField
                      {...register('postalCode', {
                        required: 'Postal Code is required',
                        pattern: {
                          value: /^(?=.{6,8}$)[A-Z0-9]{2,4}\s[A-Z0-9]{2,4}$/i,
                          message: 'Enter 6 to 8 alphanumeric characters including a space (e.g., AB1 2XY or MMM MMM)'
                        }
                      })}
                      {...addValidationBehaviors('postalCode')}
                      slotProps={{
                        htmlInput: { ['data-testid']: 'postalCode' }
                      }}
                      label="Postal Code"
                      type="text"
                      fullWidth
                    />
                  ) : (
                    <TextField
                      {...register('postalCode', {
                        required: 'Zip Code is required',
                        pattern: {
                          value: /^\d{5}(-\d{4})?$/,
                          message: 'Enter a valid 5 or 9-digit ZIP code (e.g., 12345 or 12345-6789)'
                        }
                      })}
                      {...addValidationBehaviors('postalCode')}
                      slotProps={{
                        htmlInput: { ['data-testid']: 'zip' }
                      }}
                      label="Zip Code"
                      type="text"
                      fullWidth
                    />
                  )}
                </Stack>
                {showAdditionalLocationFields && (
                  <Stack spacing={'var(--spacing-l)'} flex={1}>
                    <TextField
                      {...register('county', {})}
                      {...addValidationBehaviors('county')}
                      slotProps={{
                        htmlInput: { ['data-testid']: 'county' }
                      }}
                      label="County"
                      fullWidth
                    />
                    <Controller
                      name="timeZone"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          value={field.value ?? ''}
                          onChange={e => {
                            field.onChange(e.target.value)
                          }}
                          {...addValidationBehaviors('timeZone')}
                          slotProps={{
                            // @ts-expect-error - select doesn't expose the data attributes but this is valid
                            select: { ['data-testid']: 'timeZone' }
                          }}
                          select
                          label="Time Zone (optional)"
                        >
                          {VALID_TIME_ZONES.map(type => {
                            return (
                              <MenuItem key={type} data-testid="timeZone_value" value={type}>
                                {type}
                              </MenuItem>
                            )
                          })}
                        </TextField>
                      )}
                    />
                    <TextField
                      {...register('crossStreet', {})}
                      {...addValidationBehaviors('crossStreet')}
                      slotProps={{
                        htmlInput: { ['data-testid']: 'crossStreet' }
                      }}
                      label="Cross Street (optional)"
                      fullWidth
                    />
                  </Stack>
                )}{' '}
              </Stack>
            </Stack>
          </Stack>
        </Stack>
        <Divider sx={{ borderColor: 'var(--bg-surface-low)' }} />
        <Stack direction={'row'} spacing={'var(--spacing-m)'} sx={{ justifyContent: 'flex-end', p: 'var(--spacing-m)' }}>
          <Button variant="outlined" onClick={handleCancel} data-testid={'cancel-address-change-button'}>
            Cancel
          </Button>
          <Button loading={isLoading} variant="contained" color="primary" type="submit" data-testid={'first-next-address-change-button'}>
            Continue
          </Button>
        </Stack>
      </Stack>
    </form>
  )
}
